import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { TableProps } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  RedoOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { FolderOpen } from 'lucide-react';

import { DataTable, MenuThaoTac, PageHeader, SplitPane, TabsTruot, ThongBaoHetHanMuc } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { tachLoiHanMuc } from '../../../lib/loi-han-muc';
import type { ApiError } from '../../../lib/errors';
import { ngayGioVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { NHAN_LOAI } from '../../cau-hinh-ocr/constants';
import type { OcrTable } from '../../cau-hinh-ocr/types';
import { OcrStageBadge, StatusBadge } from '../components/badges';
import { XemBanChep } from '../components/XemBanChep';
import { XemTruocTep } from '../components/XemTruocTep';
import { QUET_LAI_DUOC, SUA_DUOC, XOA_DUOC } from '../constants';
import { useBanChepChungTu, useChiTietChungTu, useGhiChungTu, useTepChungTu } from '../hooks/useChungTu';
import { useTenNguoi } from '../hooks/useTenNguoi';
import { useThaoTacChungTu } from '../hooks/useThaoTacChungTu';
import { giaTriO, gomDongTheoBang } from '../nghiep-vu/tai-len';
import type { DocDetail, DocLineItem, DocValue } from '../types';

/** Số và tiền hiện theo locale; giá trị không phải số giữ nguyên văn (dữ liệu nghiệp vụ). */
function hienSo(v: unknown, locale: string): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? soVN(n, locale) : String(v);
}

/** Khung trái: tệp gốc (chính và tệp kèm) và bản chép OCR. */
function KhungTep({ doc }: { doc: DocDetail }) {
  const { t } = useT();
  const [extra, setExtra] = useState<number | undefined>(undefined);
  const tep = useTepChungTu(doc.id, extra);
  const [moBanChep, setMoBanChep] = useState(false);
  const banChep = useBanChepChungTu(doc.id, moBanChep);
  const dangXem = extra === undefined ? { ten: doc.file_name, mime: doc.mime_type } : {
    ten: doc.extra_files[extra]?.file_name ?? null,
    mime: doc.extra_files[extra]?.mime_type ?? null,
  };

  return (
    <div className="flex h-full min-h-[480px] flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      <TabsTruot
        gon
        className="px-4"
        onChange={(k) => k === 'ban-chep' && setMoBanChep(true)}
        items={[
          {
            key: 'tep',
            label: t('chungTu.theTepGoc'),
            children: (
              <div className="flex h-full min-h-[420px] flex-col">
                {doc.extra_files.length > 0 && (
                  <div className="border-b border-default px-4 pb-3">
                    <Select<number>
                      className="w-full sm:w-72"
                      aria-label={t('chungTu.theTepGoc')}
                      value={extra ?? -1}
                      onChange={(v) => setExtra(v < 0 ? undefined : v)}
                      options={[
                        { value: -1, label: `${t('chungTu.tepChinh')}: ${doc.file_name ?? t('chungTu.khongTen')}` },
                        ...doc.extra_files.map((f) => ({ value: f.index, label: f.file_name ?? t('ocr.tepSo', { so: f.index + 2 }) })),
                      ]}
                    />
                  </div>
                )}
                <div className="min-h-0 flex-1">
                  <XemTruocTep
                    tep={tep.data}
                    dangTai={tep.isLoading}
                    loi={tep.error && !tep.data ? tep.error : null}
                    onThuLai={() => void tep.refetch()}
                    tenTep={dangXem.ten}
                    mime={dangXem.mime}
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'ban-chep',
            label: t('chungTu.theBanChep'),
            disabled: !doc.has_transcript,
            children: (
              <XemBanChep
                banChep={banChep.data}
                dangTai={banChep.isLoading}
                loi={banChep.error && !banChep.data ? banChep.error : null}
                onThuLai={() => void banChep.refetch()}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

/** Một ô giá trị trường: sửa tại chỗ khi được sửa, chỉ đọc khi không. */
function OGiaTri({
  kieu,
  giaTri,
  nhan,
  suaDuoc,
  onDoi,
}: {
  kieu: DocValue['field']['data_type'];
  giaTri: string | null;
  nhan: string;
  suaDuoc: boolean;
  onDoi: (v: string | null) => void;
}) {
  const { t, locale } = useT();
  if (!suaDuoc) {
    if (giaTri === null || giaTri === '') {
      return <span className="text-xs text-content-muted">{t('chungTu.chuaNhanDang')}</span>;
    }
    if (kieu === 'BOOLEAN') return <span className="text-sm text-content-primary">{giaTri === 'true' ? t('chungTu.co') : t('chungTu.khong')}</span>;
    if (kieu === 'CURRENCY' || kieu === 'NUMBER') {
      return <span className="font-mono text-sm tabular-nums text-content-primary">{hienSo(giaTri, locale)}</span>;
    }
    if (kieu === 'DATE') return <span className="text-sm text-content-primary">{ngayVN(giaTri)}</span>;
    return <span className="break-words text-sm text-content-primary">{giaTri}</span>;
  }
  if (kieu === 'BOOLEAN') {
    return (
      <Select<string>
        size="small"
        allowClear
        className="w-full"
        aria-label={nhan}
        value={giaTri ?? undefined}
        onChange={(v) => onDoi(v ?? null)}
        options={[
          { value: 'true', label: t('chungTu.co') },
          { value: 'false', label: t('chungTu.khong') },
        ]}
      />
    );
  }
  return (
    <Input
      size="small"
      aria-label={nhan}
      className={kieu === 'CURRENCY' || kieu === 'NUMBER' ? 'font-mono' : undefined}
      placeholder={kieu === 'DATE' ? 'yyyy-mm-dd' : undefined}
      value={giaTri ?? ''}
      onChange={(e) => onDoi(e.target.value === '' ? null : e.target.value)}
    />
  );
}

/**
 * Khung phải: thông tin chứng từ, giá trị từng trường (sửa được ở Nháp, Chờ xác nhận), các bảng.
 * Phía gọi gắn `key` theo `updated_at`, nên bản nháp tự nạp lại khi chứng từ đổi trên server.
 */
function KhungDuLieu({ doc, coSua }: { doc: DocDetail; coSua: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const tenNguoi = useTenNguoi();
  const ghi = useGhiChungTu();
  const [nhap, setNhap] = useState<Record<string, string | null>>({});

  const suaDuoc = coSua && SUA_DUOC.includes(doc.status);
  const theoTruong = useMemo(() => new Map(doc.values.map((v) => [v.field_id, v])), [doc.values]);
  const daDoi = Object.keys(nhap).filter((id) => (theoTruong.get(id)?.string_value ?? null) !== nhap[id]);
  const bang = useMemo(
    () => gomDongTheoBang(doc.schema.tables.map((tb) => tb.table_key), doc.line_items),
    [doc.schema.tables, doc.line_items],
  );

  const luu = () =>
    ghi.sua.mutate(
      { id: doc.id, du: { values: daDoi.map((field_id) => ({ field_id, string_value: nhap[field_id] ?? null })) } },
      {
        onSuccess: () => toast.success(t('chungTu.daLuuGiaTri')),
        onError: (e) => toast.error(dichLoi(e)),
      },
    );

  const nguoiLuc = (id: string | null, luc: string | null) =>
    id && luc ? t('chungTu.nguoiLuc', { ten: tenNguoi(id) ?? '—', luc: ngayGioVN(luc, locale) }) : '—';

  const thongTin: Array<[string, string]> = [
    [t('chungTu.ttLoaiChungTu'), `${doc.schema.name} · ${t(NHAN_LOAI[doc.schema.type])}`],
    [t('chungTu.ttSoChungTu'), doc.invoice_number ?? '—'],
    [t('chungTu.ttNgayChungTu'), doc.issue_date ? ngayVN(doc.issue_date) : '—'],
    [t('chungTu.ttNguoiBan'), doc.seller_name ?? '—'],
    [t('chungTu.ttMstNguoiBan'), doc.seller_tax_code ?? '—'],
    [t('chungTu.ttTongThanhToan'), hienSo(doc.grand_total, locale)],
    [t('chungTu.ttDoTinCay'), doc.ocr_confidence === null ? '—' : `${soVN(Math.round(doc.ocr_confidence * 1000) / 10, locale)}%`],
    [t('chungTu.ttTaiLenBoi'), nguoiLuc(doc.created_by, doc.created_at)],
    [t('chungTu.ttXacNhanBoi'), nguoiLuc(doc.confirmed_by, doc.confirmed_at)],
    [t('chungTu.ttChuyenBoi'), nguoiLuc(doc.processed_by, doc.processed_at)],
  ];

  const truong = [...doc.schema.fields].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="flex h-full flex-col gap-4 lg:overflow-y-auto">
      <section aria-label={t('chungTu.thongTinChung')} className="rounded-lg border border-default bg-surface shadow-sm">
        <h2 className="border-b border-default px-5 py-3 text-sm font-semibold text-content-primary">{t('chungTu.thongTinChung')}</h2>
        <dl className="grid gap-x-4 gap-y-2 px-5 py-4 text-xs sm:grid-cols-2">
          {thongTin.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="text-content-muted">{k}</dt>
              <dd className="break-words text-sm text-content-primary">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-label={t('chungTu.truongDuLieu')} className="rounded-lg border border-default bg-surface shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-default px-5 py-3">
          <h2 className="text-sm font-semibold text-content-primary">{t('chungTu.truongDuLieu')}</h2>
          <span className="text-xs text-content-muted">{t('chungTu.soTruong', { so: truong.length })}</span>
        </div>
        {!suaDuoc && coSua && doc.status !== 'PROCESSING' && (
          <p className="border-b border-default px-5 py-2 text-xs text-content-muted">{t('chungTu.chiDoc')}</p>
        )}
        {truong.length === 0 ? (
          <Empty icon={FileTextOutlined} title={t('chungTu.chuaCoTruong')} />
        ) : (
          <div className="divide-y divide-[var(--border-default)]">
            {truong.map((f) => {
              const v = theoTruong.get(f.id);
              const giaTri = f.id in nhap ? nhap[f.id]! : (v?.string_value ?? null);
              return (
                <div key={f.id} className="grid gap-1 px-5 py-2.5 sm:grid-cols-[minmax(8rem,12rem),1fr] sm:items-center sm:gap-3">
                  <div className="min-w-0">
                    <span className="block truncate text-xs text-content-secondary" title={f.label}>
                      {f.label}
                    </span>
                    {v?.is_manually_edited && <span className="text-2xs text-content-muted">{t('chungTu.daSuaTay')}</span>}
                  </div>
                  <div className="min-w-0">
                    <OGiaTri
                      kieu={f.data_type}
                      giaTri={giaTri}
                      nhan={f.label}
                      suaDuoc={suaDuoc}
                      onDoi={(x) => setNhap((cu) => ({ ...cu, [f.id]: x }))}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {suaDuoc && daDoi.length > 0 && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-default px-5 py-3">
            <Button variant="ghost" size="sm" onClick={() => setNhap({})} disabled={ghi.sua.isPending}>
              {t('chungTu.boThayDoi')}
            </Button>
            <Button variant="primary" size="sm" icon={<SaveOutlined />} loading={ghi.sua.isPending} onClick={luu}>
              {ghi.sua.isPending ? t('chung.dangLuu') : t('chungTu.luuGiaTri')}
            </Button>
          </div>
        )}
      </section>

      {doc.schema.tables.map((tb) => (
        <BangDong key={tb.id} bang={tb} dong={bang.get(tb.table_key) ?? []} />
      ))}
    </div>
  );
}

/** Một bảng của chứng từ, cột theo cấu hình loại chứng từ. Chỉ đọc. */
function BangDong({ bang, dong }: { bang: OcrTable; dong: DocLineItem[] }) {
  const { t, locale } = useT();
  const cot = [...bang.columns].sort((a, b) => a.display_order - b.display_order);
  const columns: TableProps<DocLineItem>['columns'] = [
    {
      title: t('chung.stt'),
      dataIndex: 'stt',
      width: 56,
      render: (v: number) => <span className="text-xs tabular-nums text-content-muted">{v}</span>,
    },
    ...cot.map((c) => ({
      title: c.label,
      key: c.column_key,
      align: c.data_type === 'NUMBER' || c.data_type === 'CURRENCY' ? ('right' as const) : undefined,
      render: (_v: unknown, row: DocLineItem) => {
        const g = giaTriO(row, c.column_key);
        return c.data_type === 'NUMBER' || c.data_type === 'CURRENCY' ? (
          <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">{hienSo(g, locale)}</span>
        ) : (
          <span className="text-xs text-content-primary">{g === null || g === undefined ? '—' : String(g)}</span>
        );
      },
    })),
  ];
  return (
    <section aria-label={bang.name} className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-default px-5 py-3">
        <h2 className="min-w-0 truncate text-sm font-semibold text-content-primary">{bang.name}</h2>
        <span className="text-xs text-content-muted">{t('chungTu.soDong', { so: soVN(dong.length, locale) })}</span>
      </div>
      <DataTable<DocLineItem>
        caption={t('chungTu.bangCaptionDong', { ten: bang.name })}
        rowKey="id"
        size="small"
        dataSource={dong}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty icon={FileTextOutlined} title={t('chungTu.bangRong')} /> }}
      />
    </section>
  );
}

/**
 * Chi tiết chứng từ (`/xu-ly/chung-tu/:docId`, phân hệ `DOC_MGMT`): tệp gốc và bản chép bên trái,
 * kết quả nhận dạng bên phải. Đang quét thì màn hỏi lại `GET /documents/{id}` mỗi vài giây tới khi
 * lượt quét kết thúc.
 */
export function ChiTietChungTuView() {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useDieuHuong();
  const { t } = useT();
  const dichLoi = useLoi();
  const q = useChiTietChungTu(docId);
  const thaoTac = useThaoTacChungTu();
  const coSua = useRoutePermission('UPDATE');
  const coXoa = useRoutePermission('DELETE');
  const coChuyen = useRoutePermission('EXPORT');

  const veDanhSach = () => navigate('/xu-ly/chung-tu');
  const vun = [{ label: t('nav.nhom.xuLyTaiLieu') }, { label: t('chungTu.tieuDe'), icon: <FolderOpen size={12} />, onClick: veDanhSach }];

  if (q.isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 bg-subtle p-4">
        <Skeleton className="h-10 w-1/2" />
        <div className="grid flex-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-full min-h-[320px]" />
          <Skeleton className="h-full min-h-[320px]" />
        </div>
      </div>
    );
  }
  const doc = q.data;
  if (!doc) {
    const khongThay = (q.error as ApiError | null)?.status === 404;
    return (
      <div className="flex h-full flex-col gap-4 bg-subtle p-4">
        <PageHeader breadcrumb={vun} title={t('chungTu.chiTietTieuDe')} />
        <div className="rounded-lg border border-default bg-surface shadow-sm">
          {khongThay ? (
            <Empty
              icon={FileTextOutlined}
              title={t('chungTu.khongTimThay')}
              description={t('chungTu.khongTimThayMo')}
              action={
                <Button variant="primary" size="md" onClick={veDanhSach}>
                  {t('chungTu.veDanhSach')}
                </Button>
              }
            />
          ) : (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('chungTu.loiChiTiet', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const ten = doc.file_name || t('chungTu.khongTen');
  const ngan = { id: doc.id, ten };
  const thuLaiSuyLuanDuoc = doc.status === 'ERROR' && doc.ocr_error_step === 'MAP' && doc.has_transcript;
  const hanMuc = doc.status === 'ERROR' ? tachLoiHanMuc(doc.ocr_error) : null;
  const nutLamLai = coSua && (
    <Button
      variant="outline"
      size="sm"
      icon={thuLaiSuyLuanDuoc ? <RedoOutlined /> : <ReloadOutlined />}
      onClick={() => (thuLaiSuyLuanDuoc ? thaoTac.thuLaiSuyLuan(doc.id) : thaoTac.moQuetLai(ngan))}
    >
      {thuLaiSuyLuanDuoc ? t('chungTu.thuLaiSuyLuan') : t('chungTu.quetLaiTuDau')}
    </Button>
  );

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden bg-subtle p-4">
      <PageHeader
        breadcrumb={[...vun, { label: ten }]}
        title={<span title={ten}>{ten}</span>}
        subtitle={
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <span className="rounded bg-subtle px-2 py-0.5 font-mono text-xs text-content-secondary">{doc.schema_code}</span>
            <StatusBadge status={doc.status} />
          </span>
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={() => void q.refetch()}
            >
              <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
            </Button>
            {coSua && SUA_DUOC.includes(doc.status) && (
              <Button
                variant="primary"
                size="md"
                icon={<CheckOutlined />}
                loading={thaoTac.dangXacNhan}
                onClick={() => thaoTac.xacNhan(doc.id)}
              >
                {t('chungTu.xacNhanChungTu')}
              </Button>
            )}
            {coChuyen && doc.status === 'CONFIRMED' && (
              <Button variant="primary" size="md" icon={<SendOutlined />} onClick={() => thaoTac.moChuyen(ngan)}>
                {t('chungTu.chuyenVaoKho')}
              </Button>
            )}
            <MenuThaoTac
              tenBanGhi={ten}
              muc={[
                {
                  khoa: 'thu-lai',
                  nhan: t('chungTu.thuLaiSuyLuan'),
                  moTa: t('chungTu.thuLaiSuyLuanMo'),
                  icon: <RedoOutlined />,
                  hien: coSua && thuLaiSuyLuanDuoc,
                  onChon: () => thaoTac.thuLaiSuyLuan(doc.id),
                },
                {
                  khoa: 'quet-lai',
                  nhan: t('chungTu.quetLaiTuDau'),
                  moTa: t('chungTu.quetLaiMo'),
                  icon: <ReloadOutlined />,
                  hien: coSua && QUET_LAI_DUOC.includes(doc.status),
                  onChon: () => thaoTac.moQuetLai(ngan),
                },
                {
                  khoa: 'huy-quet',
                  nhan: t('chungTu.huyLuotQuet'),
                  icon: <StopOutlined />,
                  hien: coSua && doc.status === 'PROCESSING',
                  nhom: 'pha-huy',
                  onChon: () => thaoTac.moHuyQuet(ngan),
                },
                {
                  khoa: 'xoa',
                  nhan: t('chungTu.xoaChungTu'),
                  icon: <DeleteOutlined />,
                  hien: coXoa && XOA_DUOC.includes(doc.status),
                  nhom: 'pha-huy',
                  onChon: () => thaoTac.moXoa(ngan, veDanhSach),
                },
              ]}
            />
          </>
        }
      />

      {doc.status === 'PROCESSING' && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5">
          <LoadingOutlined spin className="text-primary-600" />
          <span className="text-sm text-primary-700">{t('chungTu.dangQuet')}</span>
          <OcrStageBadge doc={doc} />
        </div>
      )}
      {hanMuc && (
        <ThongBaoHetHanMuc
          thongTin={hanMuc}
          nhanThuLai={coSua ? (thuLaiSuyLuanDuoc ? t('chungTu.thuLaiSuyLuan') : t('chungTu.quetLaiTuDau')) : undefined}
          nutThuLai={nutLamLai}
        />
      )}
      {doc.status === 'ERROR' && !hanMuc && (
        <div role="alert" className="flex flex-wrap items-start gap-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-2.5">
          <ExclamationCircleOutlined className="mt-0.5 text-danger-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-danger-700">
              {t('chungTu.loiQuet', { buoc: doc.ocr_error_step === 'READ' ? t('chungTu.buocDoc') : t('chungTu.buocSuyLuan') })}
            </p>
            {doc.ocr_error && <p className="mt-0.5 break-words text-xs text-danger-700">{doc.ocr_error}</p>}
          </div>
          {nutLamLai}
        </div>
      )}

      <SplitPane
        left={<KhungTep doc={doc} />}
        right={<KhungDuLieu key={doc.updated_at} doc={doc} coSua={coSua} />}
        ariaLabel={t('chungTu.keoDeDoiDoRong')}
      />

      {thaoTac.hopThoai}
    </div>
  );
}

